import { Array, pipe, Result } from 'effect'
import { isNotUndefined } from 'effect/Predicate'
import { t } from 'i18next'

import type { RSheetColumn, RSheetRecordNode } from '../../domain/column'
import type { CopyMatrix } from '../../domain/copyMatrix'
import type { RSheetsCursor } from '../../domain/cursor/cursorTypes'
import { generateChangeCandidates } from '../../domain/generateChangesFromCopy/generateChangeCandidates'
import type { NewRecordChange } from '../action/addNewRecord/addNewRecords'
import { getReferencedStreamNameNoOp } from './getReferencedStreamName'
import type { RecordChange } from './updateViewRecords'

interface Argument {
  copyMatrix: CopyMatrix
  records: RSheetRecordNode[]
  columns: Array<RSheetColumn<RSheetRecordNode>>
  cursor: RSheetsCursor | undefined
}

interface Result {
  newRecordChanges: NewRecordChange[]
  recordChanges: RecordChange[]
  errors: Array<{ message: string }>
}

export const generateDeleteChangesFromArea = (argument: Argument): Result => {
  // 削除では値を使わないため、参照先 stream 名の解決は不要（id ベースペーストの判定は moot）
  const { updates } = generateChangeCandidates({
    ...argument,
    getReferencedStreamName: getReferencedStreamNameNoOp,
  })

  const [recordChanges, errors] = pipe(
    updates,
    Array.map(({ column, rowIndex, innerRowIndex }) => generateDeleteChange(column, rowIndex, innerRowIndex)),
    Array.separate,
  )
  return {
    newRecordChanges: [],
    recordChanges,
    errors: errors.filter(isNotUndefined),
  }
}

const generateDeleteChange = (
  column: RSheetColumn<RSheetRecordNode>,
  rowIndex: number,
  nestedColumnIndex: number,
): Result.Result<RecordChange, { message: string }> => {
  if (column.readonly === true) {
    return Result.fail({
      message: t(`このカラムは書き込み禁止です`),
    })
  }
  return Result.succeed({
    rowIndex,
    innerRowIndex: nestedColumnIndex,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    nodePath: column.field!.nodePath,
    isNestedColumn: column.node.path.length > 1,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    field: column.field!,
    value: null,
    label: undefined,
  })
}
